import React, { Component } from 'react';
import { Row, Col, Card, Button, Select, Table, Tag, Modal, Form, Input, InputNumber, message } from 'antd';
import { FormInstance } from 'antd/lib/form';
import BreadcrumbCustom from '../../components/widget/BreadcrumbCustom';
import AiPromptService, {
    IAiPromptSceneOption,
    IAiPromptTemplate,
    IAiPromptTemplateSave,
} from '../../service/ai/AiPromptService';
import './page-settings.less';

interface PromptVersionPageState {
    loading: boolean;
    sceneCode?: string;
    promptType?: string;
    status?: number;
    sceneOptions: IAiPromptSceneOption[];
    templateList: IAiPromptTemplate[];
    pageNo: number;
    pageSize: number;
    total: number;
    editorVisible: boolean;
    editorMode: 'create' | 'edit';
    editingTemplate: IAiPromptTemplate | null;
    submitLoading: boolean;
}

const STATUS_OPTIONS = [
    { label: '全部', value: -1 },
    { label: '草稿', value: 0 },
    { label: '生效', value: 1 },
    { label: '归档', value: 2 },
];

const PROMPT_TYPE_OPTIONS = [
    { label: 'SYSTEM', value: 'SYSTEM' },
    { label: 'USER', value: 'USER' },
    { label: 'REFERENCE', value: 'REFERENCE' },
];

class PromptVersionPage extends Component<any, PromptVersionPageState> {
    state: PromptVersionPageState = {
        loading: false,
        sceneCode: undefined,
        promptType: undefined,
        status: -1,
        sceneOptions: [],
        templateList: [],
        pageNo: 1,
        pageSize: 10,
        total: 0,
        editorVisible: false,
        editorMode: 'create',
        editingTemplate: null,
        submitLoading: false,
    };

    async componentDidMount() {
        await this.fetchTemplates(1, this.state.pageSize);
    }

    formRef = React.createRef<FormInstance>();

    buildQuery = () => ({
        sceneCode: this.state.sceneCode,
        promptType: this.state.promptType,
        status: this.state.status === -1 ? undefined : this.state.status,
    });

    fetchTemplates = async (pageNo: number, pageSize: number) => {
        this.setState({ loading: true });
        const response = await AiPromptService.getPromptTemplateList(this.buildQuery(), pageNo, pageSize);
        if (response && response.success) {
            const pageData = response?.data?.promptTemplateList || {};
            this.setState({
                templateList: pageData.records || [],
                total: pageData.total || 0,
                pageNo: pageData.current || pageNo,
                pageSize: pageData.size || pageSize,
                sceneOptions: response?.data?.sceneOptions || [],
            });
        } else {
            message.error(response ? response.message : '获取提示词版本失败');
        }
        this.setState({ loading: false });
    };

    getSceneName = (sceneCode: string) => {
        const target = this.state.sceneOptions.find((item) => item.code === sceneCode);
        return target ? target.name : sceneCode;
    };

    renderStatus = (status: number) => {
        if (status === 1) {
            return <Tag color="green">生效</Tag>;
        }
        if (status === 2) {
            return <Tag>归档</Tag>;
        }
        return <Tag color="gold">草稿</Tag>;
    };

    handleOpenCreate = () => {
        const defaultScene = this.state.sceneOptions.length > 0 ? this.state.sceneOptions[0].code : '';
        this.setState({ editorVisible: true, editorMode: 'create', editingTemplate: null }, () => {
            this.formRef.current?.resetFields();
            this.formRef.current?.setFieldsValue({
                sceneCode: defaultScene,
                promptType: 'SYSTEM',
                source: 'MANUAL',
            });
        });
    };

    handleOpenEdit = (record: IAiPromptTemplate) => {
        this.setState({ editorVisible: true, editorMode: 'edit', editingTemplate: record }, () => {
            this.formRef.current?.setFieldsValue({
                sceneCode: record.sceneCode,
                promptType: record.promptType,
                title: record.title,
                content: record.content,
                model: record.model,
                temperature: record.temperature,
                source: record.source || 'MANUAL',
                summary: record.summary,
            });
        });
    };

    handleCloseEditor = () => {
        this.setState({ editorVisible: false, editingTemplate: null });
    };

    handleSave = async () => {
        if (!this.formRef.current) {
            return;
        }
        try {
            const values = await this.formRef.current.validateFields();
            this.setState({ submitLoading: true });
            const payload: IAiPromptTemplateSave = {
                sceneCode: values.sceneCode,
                promptType: values.promptType,
                title: values.title,
                content: values.content,
                model: values.model,
                temperature: values.temperature,
                source: values.source,
                summary: values.summary,
            };
            let response = null;
            if (this.state.editorMode === 'create') {
                response = await AiPromptService.createPromptTemplate(payload);
            } else if (this.state.editingTemplate) {
                response = await AiPromptService.updatePromptTemplate(this.state.editingTemplate.id, payload);
            }

            if (response && response.success) {
                message.success(this.state.editorMode === 'create' ? '提示词版本已创建' : '提示词版本已更新');
                this.handleCloseEditor();
                await this.fetchTemplates(this.state.pageNo, this.state.pageSize);
            } else {
                message.error(response ? response.message : '保存提示词版本失败');
            }
        } catch (e) {
            return;
        } finally {
            this.setState({ submitLoading: false });
        }
    };

    handleActivate = (record: IAiPromptTemplate) => {
        Modal.confirm({
            title: '确认发布该版本？',
            content: '发布后同场景同类型的当前生效版本会自动归档。',
            okText: '发布',
            cancelText: '取消',
            onOk: async () => {
                const response = await AiPromptService.activatePromptTemplate(record.id);
                if (response && response.success) {
                    message.success('提示词版本已发布');
                    await this.fetchTemplates(this.state.pageNo, this.state.pageSize);
                } else {
                    message.error(response ? response.message : '发布失败');
                }
            },
        });
    };

    handleArchive = (record: IAiPromptTemplate) => {
        Modal.confirm({
            title: '确认归档该版本？',
            okText: '归档',
            cancelText: '取消',
            onOk: async () => {
                const response = await AiPromptService.archivePromptTemplate(record.id);
                if (response && response.success) {
                    message.success('提示词版本已归档');
                    await this.fetchTemplates(this.state.pageNo, this.state.pageSize);
                } else {
                    message.error(response ? response.message : '归档失败');
                }
            },
        });
    };

    render() {
        const columns: any[] = [
            {
                title: 'ID',
                dataIndex: 'id',
                width: 80,
            },
            {
                title: '场景',
                dataIndex: 'sceneCode',
                width: 220,
                render: (sceneCode: string) => this.getSceneName(sceneCode),
            },
            {
                title: '类型',
                dataIndex: 'promptType',
                width: 120,
            },
            {
                title: '版本',
                dataIndex: 'versionNo',
                width: 90,
            },
            {
                title: '状态',
                dataIndex: 'status',
                width: 90,
                render: (status: number) => this.renderStatus(status),
            },
            {
                title: '标题',
                dataIndex: 'title',
                width: 180,
            },
            {
                title: '模型',
                dataIndex: 'model',
                width: 180,
            },
            {
                title: '内容',
                dataIndex: 'content',
                render: (content: string) => <div className="settings-content-preview">{content || '-'}</div>,
            },
            {
                title: '更新时间',
                dataIndex: 'lastEditTime',
                width: 170,
            },
            {
                title: '操作',
                width: 220,
                fixed: 'right',
                render: (_: any, record: IAiPromptTemplate) => (
                    <>
                        <Button
                            type="link"
                            size="small"
                            disabled={record.status === 1}
                            onClick={() => this.handleOpenEdit(record)}
                        >
                            编辑
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            disabled={record.status === 1}
                            onClick={() => this.handleActivate(record)}
                        >
                            发布
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            disabled={record.status === 2}
                            onClick={() => this.handleArchive(record)}
                        >
                            归档
                        </Button>
                    </>
                ),
            },
        ];

        return (
            <div className="page-settings">
                <BreadcrumbCustom breads={['系统设置', '提示词版本管理']} />
                <Row gutter={5}>
                    <Col md={23}>
                        <Card title="提示词版本管理" bordered={false}>
                            <div className="settings-filter-row">
                                <div className="settings-filter-item">
                                    <span className="settings-filter-label">场景</span>
                                    <Select
                                        style={{ width: 220 }}
                                        allowClear
                                        value={this.state.sceneCode}
                                        placeholder="全部场景"
                                        onChange={(value) => this.setState({ sceneCode: value })}
                                    >
                                        {this.state.sceneOptions.map((item) => (
                                            <Select.Option key={item.code} value={item.code}>{item.name}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="settings-filter-item">
                                    <span className="settings-filter-label">类型</span>
                                    <Select
                                        style={{ width: 160 }}
                                        allowClear
                                        value={this.state.promptType}
                                        placeholder="全部类型"
                                        onChange={(value) => this.setState({ promptType: value })}
                                    >
                                        {PROMPT_TYPE_OPTIONS.map((item) => (
                                            <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="settings-filter-item">
                                    <span className="settings-filter-label">状态</span>
                                    <Select
                                        style={{ width: 160 }}
                                        value={this.state.status}
                                        onChange={(value) => this.setState({ status: value })}
                                    >
                                        {STATUS_OPTIONS.map((item) => (
                                            <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className="settings-filter-actions">
                                    <Button onClick={() => this.fetchTemplates(1, this.state.pageSize)}>查询</Button>
                                    <Button type="primary" onClick={this.handleOpenCreate}>新建版本</Button>
                                </div>
                            </div>

                            <Table
                                rowKey="id"
                                loading={this.state.loading}
                                columns={columns}
                                dataSource={this.state.templateList}
                                scroll={{ x: 1400 }}
                                pagination={{
                                    current: this.state.pageNo,
                                    pageSize: this.state.pageSize,
                                    total: this.state.total,
                                    showSizeChanger: true,
                                    showQuickJumper: true,
                                }}
                                onChange={(pagination) => {
                                    this.fetchTemplates(pagination.current || 1, pagination.pageSize || this.state.pageSize);
                                }}
                            />
                        </Card>
                    </Col>
                </Row>

                <Modal
                    title={this.state.editorMode === 'create' ? '新建提示词版本' : '编辑提示词版本'}
                    visible={this.state.editorVisible}
                    width={920}
                    okText="保存"
                    cancelText="取消"
                    confirmLoading={this.state.submitLoading}
                    onOk={this.handleSave}
                    onCancel={this.handleCloseEditor}
                >
                    <Form ref={this.formRef} layout="vertical">
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="场景"
                                    name="sceneCode"
                                    rules={[{ required: true, message: '请选择场景' }]}
                                >
                                    <Select disabled={this.state.editorMode === 'edit'}>
                                        {this.state.sceneOptions.map((item) => (
                                            <Select.Option key={item.code} value={item.code}>{item.name}</Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label="类型"
                                    name="promptType"
                                    rules={[{ required: true, message: '请选择提示词类型' }]}
                                >
                                    <Select disabled={this.state.editorMode === 'edit'}>
                                        {PROMPT_TYPE_OPTIONS.map((item) => (
                                            <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item label="标题" name="title">
                                    <Input maxLength={255} placeholder="版本标题（可选）" />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item label="模型" name="model">
                                    <Input placeholder="可选，空则用全局默认" />
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item label="Temperature" name="temperature">
                                    <InputNumber min={0} max={2} step={0.1} style={{ width: '100%' }} />
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item label="来源" name="source">
                                    <Select>
                                        <Select.Option value="MANUAL">MANUAL</Select.Option>
                                        <Select.Option value="AI">AI</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item
                            label="提示词内容"
                            name="content"
                            rules={[{ required: true, message: '请输入提示词内容' }]}
                        >
                            <Input.TextArea rows={12} placeholder="请输入提示词正文" />
                        </Form.Item>
                        <Form.Item label="版本说明" name="summary">
                            <Input.TextArea rows={3} placeholder="可填写改动说明、适用范围等" />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        );
    }
}

export default PromptVersionPage;
